import { base44 } from '@/api/base44Client';

// ---------------------------------------------------------------------------
// Venue kiosk helpers — check-in, dispatch and fleet state for /venue/kiosk
// ---------------------------------------------------------------------------

export const FFB_PRESETS = [
  { label: 'Rookie — 60%', value: 60 },
  { label: 'Sport — 80%', value: 80 },
  { label: 'Pro — 100%', value: 100 },
];

export const PEDAL_PROFILES = [
  { label: 'Linear Soft', value: 'linear_soft' },
  { label: 'Medium Load-Cell', value: 'medium_load_cell' },
  { label: 'Stiff Pro', value: 'stiff_pro' },
];

export const PEDAL_LABELS = Object.fromEntries(PEDAL_PROFILES.map((p) => [p.value, p.label]));

export const RIG_STATUS_META = {
  READY: {
    label: 'READY',
    badge: 'bg-green-500/15 text-green-400 border-green-500/40',
    dot: 'bg-green-500',
    frame: 'border-green-500/40',
  },
  LIVE: {
    label: 'LIVE',
    badge: 'bg-blue-500/15 text-blue-400 border-blue-500/40',
    dot: 'bg-blue-500 animate-pulse',
    frame: 'border-blue-500/60',
  },
  IN_USE: {
    label: 'IN USE',
    badge: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40',
    dot: 'bg-cyan-400 animate-pulse',
    frame: 'border-cyan-500/50',
  },
  CLEANING_REQUIRED: {
    label: 'CLEANING REQUIRED',
    badge: 'bg-amber-500/15 text-amber-400 border-amber-500/40',
    dot: 'bg-amber-500',
    frame: 'border-amber-500/50',
  },
  OUT_OF_SERVICE: {
    label: 'OUT OF SERVICE',
    badge: 'bg-red-500/15 text-red-400 border-red-500/40',
    dot: 'bg-red-500',
    frame: 'border-red-500/50',
  },
};

export const BOOKING_STATUS_META = {
  confirmed: { label: 'CONFIRMED', badge: 'bg-white/5 text-white/60 border-white/15' },
  checked_in: { label: 'CHECKED-IN', badge: 'bg-amber-500/15 text-amber-400 border-amber-500/40' },
  active: { label: 'ACTIVE', badge: 'bg-blue-500/15 text-blue-400 border-blue-500/40' },
  completed: { label: 'COMPLETED', badge: 'bg-green-500/15 text-green-400 border-green-500/40' },
};

const HEARTBEAT_MS = 6000;
// A car still on track keeps a bay occupied for a short grace window after its
// last packet, so a brief gap in telemetry doesn't flash the bay as free.
const IN_USE_MS = 15000;

/** Today's date as YYYY-MM-DD in the venue's local time. */
export function todayLocalISO() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

/** Short display reference derived from the booking id. */
export function bookingReference(booking) {
  return `BK-${String(booking?.id || '').slice(-6).toUpperCase()}`;
}

/** Booked drive time in minutes (Booking.duration is stored in hours). */
export function bookingDurationMinutes(booking) {
  const hours = Number(booking?.duration) || 1;
  return Math.round(hours * 60);
}

/** Friendly experience label, falling back to a prettified key. */
export function experienceLabel(key, experiences = {}) {
  if (!key) return '—';
  return experiences[key] || key.replace(/_/g, ' ').toUpperCase();
}

/**
 * True when the bay's rig PC is reporting a running sim. A bay being driven is
 * occupied even with no booking behind it — a tester's rig is the obvious case —
 * so the floor can never read READY while a car is on track.
 */
export function isRigInUse(rig, feeds = []) {
  const feed = feeds.find((f) => f.rig_key && f.rig_key === rig?.telemetry_slot_id);
  if (!feed?.last_update) return false;
  const fresh = Date.now() - new Date(feed.last_update).getTime() < IN_USE_MS;
  return fresh && feed.is_live !== false;
}

/**
 * Effective floor state — the maintenance flag always wins, and a bay whose rig
 * is actively reporting a running sim is IN USE rather than free.
 */
export function effectiveRigStatus(rig, feeds = []) {
  if (rig?.maintenance) return 'OUT_OF_SERVICE';
  const status = rig?.operational_status || 'READY';
  if (status === 'READY' && isRigInUse(rig, feeds)) return 'IN_USE';
  return status;
}

/** True when a bay's session timer has run out. */
export function isRigExpired(rig) {
  return (
    rig?.operational_status === 'LIVE' &&
    !!rig?.session_expires_at &&
    new Date(rig.session_expires_at).getTime() <= Date.now()
  );
}

/** ms remaining on a live session (0 when expired or not live). */
export function sessionRemainingMs(rig) {
  if (!rig?.session_expires_at) return 0;
  return Math.max(0, new Date(rig.session_expires_at).getTime() - Date.now());
}

export function formatCountdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/** Telemetry heartbeat for a bay's PC. */
export function rigHeartbeat(rig, feeds = []) {
  const feed = feeds.find((f) => f.rig_key && f.rig_key === rig?.telemetry_slot_id);
  const fresh = !!feed?.last_update && Date.now() - new Date(feed.last_update).getTime() < HEARTBEAT_MS;
  return { feed, online: fresh };
}

function makeSessionRef() {
  return `SES-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
}

/**
 * Dispatch a booking to a bay: opens a RigSession, puts the bay live and
 * checks the booking in. Throws if any of the three writes fails.
 */
export async function dispatchSession({ booking, rig, ffbGainPct, pedalProfile, dispatchedBy }) {
  const durationMinutes = bookingDurationMinutes(booking);
  const startedAt = new Date();
  const expiresAt = new Date(startedAt.getTime() + durationMinutes * 60000);

  await base44.entities.RigSession.create({
    session_id: makeSessionRef(),
    rig_id: rig.id,
    rig_name: rig.name,
    bay_number: rig.bay_number,
    booking_id: booking.id,
    driver_name: booking.customer_name,
    ffb_gain_pct: ffbGainPct,
    pedal_profile: pedalProfile,
    duration_minutes: durationMinutes,
    started_at: startedAt.toISOString(),
    status: 'DISPATCHED',
    dispatched_by: dispatchedBy,
  });

  await base44.entities.BookingRig.update(rig.id, {
    operational_status: 'LIVE',
    current_driver: booking.customer_name,
    session_expires_at: expiresAt.toISOString(),
  });

  await base44.entities.Booking.update(booking.id, { status: 'checked_in' });
}

/** Close a finished session and hand the bay over for cleaning. */
export async function expireRigSession(rig) {
  await base44.entities.BookingRig.update(rig.id, {
    operational_status: 'CLEANING_REQUIRED',
    current_driver: '',
    session_expires_at: null,
  });

  const open = await base44.entities.RigSession.filter({ rig_id: rig.id, status: 'DISPATCHED' });
  const endedAt = new Date().toISOString();
  for (const session of open || []) {
    await base44.entities.RigSession.update(session.id, { status: 'COMPLETED', ended_at: endedAt });
    if (session.booking_id) {
      await base44.entities.Booking.update(session.booking_id, { status: 'completed', completed_at: endedAt });
    }
  }
}

/** Sanitisation done — bay is free again. */
export async function markSanitised(rig) {
  await base44.entities.BookingRig.update(rig.id, {
    operational_status: 'READY',
    current_driver: '',
    session_expires_at: null,
  });
}

/** Log a fault against a bay. */
export async function logRigFault({ rig, description, user }) {
  await base44.entities.RigFault.create({
    rig_id: rig.id,
    rig_name: rig.name,
    bay_number: rig.bay_number,
    description,
    reported_by: user?.email || '',
    reported_by_name: user?.full_name || '',
    reported_at: new Date().toISOString(),
    resolved: false,
  });
}